<template>
<v-container fluid>
    <v-card class="ma-sm-2" outlined>
        <v-progress-linear indeterminate color="yellow darken-2" v-if="loading || isExporting"></v-progress-linear>
        <v-card-title class="d-flex">
            <v-btn icon @click="goToDashboard()">
                <v-icon>mdi-chevron-left</v-icon>
            </v-btn> 
            {{groupInfo.name}}
            <v-btn icon @click="goToEditGroup()" class="ml-2" v-if="isSuperAdmin && !(loading || isExporting)" >
                <v-icon>mdi-pencil</v-icon>
            </v-btn>
        </v-card-title>
        <v-card-text>{{groupInfo.desc}}</v-card-text>
        <v-spacer></v-spacer>
        <group-view-filter 
            v-if="isShowGroupFilter" 
            @toCreateGroupOverview="toCreateGroupOverview($event)" 
            @toExport="toExport($event)" 
            :loading="loading" 
            :startDate="startDate" 
            :endDate="endDate" 
            :isExporting="isExporting" 
            :groupId="this.id" 
            />
    </v-card>
    <v-card class="d-flex ma-sm-2 flex-column flex-md-row flex-sm-row flex-wrap" flat tile v-if="groupInfo.isSubmit && !loading && !isExporting">
        <total-summary-component :totalSummaryInfo="totalSummaryInfo" :itemList="itemList" />
        <number-of-users-summary-component :memberNumbers="numberOfRecords" />
        <number-of-users-summary-component :memberNumbers="memberNumbers" />
        <chart-group-component :chartInfo="agesOfUsers" />
        <chart-group-component :chartInfo="chartWeight" />
        <chart-group-component :chartInfo="chartBMI" />
        <chart-group-component :chartInfo="chartPressure" />
        <chart-group-component :chartInfo="chartLDL" />
        <chart-group-component :chartInfo="chartFBS" />
        <member-list-component :userList="userList" :startDate="startDate" :endDate="endDate" :exportData="exportData" />
    </v-card>
    <v-snackbar
      v-model="snackbar"
      multi-line
    >
      {{ text }}

      <template v-slot:actions>
        <v-btn
          color="red"
          variant="text"
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
</v-container>
</template>

<script>
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import {
    mapGetters
} from "vuex";
import formatData from "../utils/formatData";
import getGroupInfoById from '../data/getGroupInfoById';
import GroupViewFilter from "../components/GroupViewFilter.vue";
import bqGetGroupSummary from "../data/bqGetGroupSummary";
import bqIsAllowAccessGroupView from "../data/bqIsAllowAccessGroupView";
import TotalSummaryComponent from "../components/TotalSummaryComponent.vue";
import NumberOfUsersSummaryComponent from "../components/NumberOfUsersSummaryComponent.vue";
import MemberListComponent from "../components/MemberListComponent.vue";
import ChartGroupComponent from "../components/ChartGroupComponent.vue";
import colors from 'vuetify/lib/util/colors';
import moment from "moment";
import bqExportData from "../data/bqExportData";
export default {
    name: 'GroupView',
    metaInfo: {
        title: 'Group View',
    },
    components: {
        ChartGroupComponent,
        GroupViewFilter,
        TotalSummaryComponent,
        NumberOfUsersSummaryComponent,
        MemberListComponent
    },
    data() {
        return {
            groupInfo: {
                // startDate: moment().subtract(10, 'd').format('YYYY-MM-DD'),
                // endDate: moment().format('YYYY-MM-DD')
            },
            //set default date from last 3 month
            isShowGroupFilter: false,
            startDate: moment().subtract(3, 'month').format('YYYY-MM-DD'),
            endDate: moment().format('YYYY-MM-DD'),
            loading: false,
            isExporting: false,
            
            numberOfRecords: {
                title: 'Activities',
                labels: [],
                series: [],
                colors: [colors.orange.lighten3, colors.green.lighten3, colors.blueGrey.lighten3, colors.purple.lighten3, colors.cyan.lighten3]
            },
            memberNumbers: {
                title: "Participants",
                labels: [],
                series: [],
                colors: [colors.cyan.lighten3, colors.cyan.darken3]
            },
            totalSummaryInfo: {
                title: "Summary",
                data: {
                    totalWeightLoss: 0,
                    totalWorkoutMins: 0,
                    totalStep: 0,
                    totalPBWF: 0
                }
            },
            agesOfUsers: {
                chartTitle: "Age groups",
                title: "Age groups",
                noData: true,
                categories: [],
                stacked: true,
                labelsShowX: true,
                labelsShowY: true,
                columnWidth: '80%',
                formatNumber: 0,
                maxY: 0,
                series: [{
                    name: 'Male',
                    data: [],
                    color: colors.cyan.lighten3
                }, {
                    name: 'Female',
                    data: [],
                    color: colors.cyan.darken3
                }]
            },
            chartWeight: {
                chartTitle: "Weight",
                title: "Weight",
                noData: true,
                categories: ["WEIGHT"],
                tickAmount: 3,
                maxY: 80,
                series: [{
                    name: 'Start',
                    color: colors.indigo.lighten3,
                    data: []
                }, {
                    name: 'End',
                    color: colors.indigo.darken3,
                    data: []
                }]
            },
            chartBMI: {
                chartTitle: "BMI",
                title: "BMI",
                noData: true,
                categories: ["BMI"],
                maxY: 35,
                score: 23,
                tickAmount: 2,
                forceNiceScale: true,
                series: [{
                    name: 'Start',
                    color: colors.green.lighten1,
                    data: []
                }, {
                    name: 'End',
                    color: colors.green.darken3,
                    data: []
                }],
                annotations: [{
                    y: 23,
                    borderColor: colors.green.lighten1,
                    offsetX: -5,
                    label: {
                        borderColor: colors.green.darken4,
                        style: {
                            color: "#FFF",
                            background: colors.green.lighten2
                        },
                        text: '23',
                        position: 'left',
                        offsetX: -10,
                        offsetY: 8
                    }
                }]
            },
            chartPressure: {
                chartTitle: "Pressure",
                title: "Pressure",
                noData: true,
                categories: ['Upper', 'Lower'],
                maxY: 150,
                scoreLower: 85,
                scoreUpper: 135,
                columnWidth: '60%',
                tickAmount: 4,
                forceNiceScale: true,
                series: [{
                    name: 'Start',
                    color: colors.deepOrange.lighten3,
                    data: []
                }, {
                    name: 'End',
                    color: colors.teal.darken3,
                    data: []
                }],
                annotations: [{
                    y: 85,
                    borderColor: colors.deepOrange.lighten2,
                    offsetX: -5,
                    label: {
                        borderColor: colors.deepOrange.darken4,
                        style: {
                            color: "#FFF",
                            background: colors.deepOrange.lighten2
                        },
                        text: '85',
                        position: 'left',
                        offsetX: -10,
                        offsetY: 8
                    }
                }, {
                    y: 135,
                    borderColor: colors.teal.lighten2,
                    offsetX: -5,
                    label: {
                        borderColor: colors.teal.darken4,
                        style: {
                            color: "#FFF",
                            background: colors.teal.lighten2
                        },
                        text: '135',
                        position: 'left',
                        offsetX: -10,
                        offsetY: 8
                    },
                }]
            },
            chartLDL: {
                chartTitle: "LDL",
                title: "LDL",
                noData: true,
                categories: ["LDL"],
                maxY: 150,
                score: 130,
                series: [{
                    name: 'Start',
                    color: colors.blue.lighten1,
                    data: []
                }, {
                    name: 'End',
                    color: colors.blue.darken3,
                    data: []
                }],
                annotations: [{
                    y: 130,
                    borderColor: colors.blue.lighten2,
                    offsetX: -5,
                    label: {
                        borderColor: colors.blue.darken4,
                        style: {
                            color: "#FFF",
                            background: colors.blue.lighten2
                        },
                        text: '130',
                        position: 'left',
                        offsetX: -10,
                        offsetY: 8
                    }
                }]
            },
            chartFBS: {
                chartTitle: "FBS",
                title: "FBS",
                noData: true,
                categories: ["FBS"],
                maxY: 150,
                score: 130,
                series: [{
                    name: 'Start',
                    color: colors.pink.lighten3,
                    data: []
                }, {
                    name: 'End',
                    color: colors.pink.darken3,
                    data: []
                }],
                annotations: [{
                    y: 130,
                    borderColor: colors.pink.lighten2,
                    offsetX: -5,
                    label: {
                        borderColor: colors.pink.darken4,
                        style: {
                            color: "#FFF",
                            background: colors.pink.lighten2
                        },
                        text: '130',
                        position: 'left',
                        offsetX: -10,
                        offsetY: 8
                    }
                }]
            },
            itemList: [{
                    key: 'totalWeightLoss',
                    title: 'Weight loss',
                    unit: 'kg',
                    icon: 'scale-bathroom',
                    color: colors.green.lighten4,
                    colorText: colors.green.darken1
                }, {
                    key: 'totalWorkoutMins',
                    title: 'Workout',
                    unit: 'mins',
                    icon: 'run',
                    color: colors.purple.lighten4,
                    colorText: colors.purple.darken1
                }, {
                    key: 'avgBMI',
                    title: 'BMI AVG',
                    unit: '',
                    icon: 'triangle-wave',
                    color: colors.pink.lighten4,
                    colorText: colors.pink.darken1
                },
                {
                    key: 'totalPBWF',
                    title: 'PBWF',
                    unit: 'serves',
                    icon: 'food-apple',
                    color: colors.orange.lighten4,
                    colorText: colors.orange.darken1
                }
            ],
            exportData: [],
            snackbar: false,
            text: `Convert to Excel failed`,
        };
    },
    created() {
        var vm = this;
        vm.$store.state.appTitle = "Thaisook Web Admin";
        firebase.auth().onAuthStateChanged((user) => {
            if (user) {
                vm.$store.dispatch("autoSignIn", user);
                this.loading = true;
                setTimeout(() => {
                    vm.initialize();
                }, "1000");
            } else {
                console.log("no user");
            }
        });

        //group id
        this.id = this.$route.params.id;
    },
    computed: {
        ...mapGetters([
            "loggedInUserId",
            "isAuthenticated",
            "isSuperAdmin",
            "isChallengeAdmin",
            "isAccessDownloadThaisookWatch"
        ])
    },
    methods: {
        initialize() {
            (async () => {
                
                //check limit access
                const isAllowed = 1;
                if( !this.isSuperAdmin ){
                    this.loading = true;
                    await bqIsAllowAccessGroupView(this.loggedInUserId, this.id);
                    this.loading = false;
                }

                if (isAllowed) {
                    this.loading = true;
                    this.groupInfo = await getGroupInfoById(this.id);
                    if (localStorage.getItem('startDate') && localStorage.getItem('endDate')) {
                        this.startDate = localStorage.getItem('startDate');
                        localStorage.removeItem('startDate');

                        this.endDate= localStorage.getItem('endDate');
                        localStorage.removeItem('endDate');
                        
                        //force to show result from previos page
                        this.toCreateGroupOverview({ startDate: this.startDate, endDate: this.endDate, isSubmit: true } );
                        this.isShowGroupFilter = true;
                    }
                    else{
                        this.toCreateGroupOverview({ startDate: this.startDate, endDate: this.endDate, isSubmit: true } );
                        this.isShowGroupFilter = true;
                    }
                    this.loading = false;
                } else {
                    this.$router.push(`/dashboard`);
                }
            })();
        },

        //router
        goToDashboard() {
            this.$router.push(`/dashboard`);
        },
        goToEditGroup() {
            this.$router.push(`/group/edit/${this.id}`);
        },
        _formatData(type, value) {
            return formatData(type, value);
        },
        calculatePercentChanges(a, b) {
            //get max value
            // return (b-a)/a*100;
            let pc = formatData('number', (b-a)/a*100);
            if ( pc > 0 ){
                pc = `+${pc}`;
            }
            return pc;
        },
        calculateMaxY(a, b, maxY, point) {
            //get max value
            let maxValue = b;
            if (a > b) {
                maxValue = a;
            }

            //compare with maxY
            if (maxValue > maxY) {
                maxY = (maxValue*1) + point;
            }
            return maxY;
        },
        toExport( obj ){
            // this.startDate = obj.startDate;
            // this.endDate = obj.endDate;
            this.isExporting = true;
            (async () => {
                const downloadUrl = await bqExportData(obj.groupId, obj.startDate, obj.endDate);
                if( downloadUrl ){
                    window.location = downloadUrl;
                    this.isExporting = false;
                }
                else{
                    this.snackbar = true;
                    this.isExporting = false;
                }
            })();
        },
        toCreateGroupOverview( obj  )  {
            this.loading = true;
            this.startDate = obj.startDate;
            this.endDate = obj.endDate;
            this.groupInfo.isSubmit = obj.isSubmit;
            if (obj.isSubmit) {
                (async () => {
                    const groupInfoSummary = await bqGetGroupSummary(this.id, obj.startDate, obj.endDate);
                    if (groupInfoSummary) {

                        //member 
                        this.memberNumbers.labels = [];
                        this.memberNumbers.series = [];
                        if (groupInfoSummary.memberNumbers != undefined) {
                            Object.entries(groupInfoSummary.memberNumbers).map(async ([key, item]) => {
                                this.memberNumbers.labels.push(formatData('capitalize', key));
                                this.memberNumbers.series.push(item);
                            });
                        }

                        //Number of records
                        this.numberOfRecords.labels = ['PHR', 'Sleep', 'Drink', 'PBWF', 'Workout'];
                        this.numberOfRecords.series = [
                            groupInfoSummary.numberOfRecords.healthData, 
                            groupInfoSummary.numberOfRecords.sleep, 
                            groupInfoSummary.numberOfRecords.water, 
                            groupInfoSummary.numberOfRecords.food,
                            groupInfoSummary.numberOfRecords.workout
                        ];

                        //Total Summary
                        this.totalSummaryInfo.data = groupInfoSummary.totalSummary;

                        //ages of users
                        // this.agesOfUsers.data = groupInfoSummary.agesOfUsers;
                        this.agesOfUsers.categories = groupInfoSummary.agesOfUsers.categories;
                        this.agesOfUsers.series[0].data = groupInfoSummary.agesOfUsers.male;
                        this.agesOfUsers.series[1].data = groupInfoSummary.agesOfUsers.female;
                        this.agesOfUsers.maxY = groupInfoSummary.agesOfUsers.maxY;
                        this.agesOfUsers.noData = false;

                        //chart weight
                        this.chartWeight.noData = true;
                        this.chartWeight.series[0].data = [];
                        this.chartWeight.percent = ''
                        if (groupInfoSummary.chartWeight.avgStartWeight) {
                            this.chartWeight.series[0].data[0] = formatData('chart', groupInfoSummary.chartWeight.avgStartWeight);
                            this.chartWeight.series[1].data[0] = formatData('chart', groupInfoSummary.chartWeight.avgEndWeight);
                            this.chartWeight.percent = this.calculatePercentChanges(
                                groupInfoSummary.chartWeight.avgStartWeight,
                                groupInfoSummary.chartWeight.avgEndWeight
                            );
                            this.chartWeight.maxY = this.calculateMaxY(
                                groupInfoSummary.chartWeight.avgStartWeight,
                                groupInfoSummary.chartWeight.avgEndWeight,
                                this.chartWeight.maxY,
                                5
                            );
                            this.chartWeight.noData = false;
                        }
                        //chart BMI
                        this.chartBMI.noData = true;
                        this.chartBMI.series[0].data = [];
                        this.chartBMI.percent = '';
                        if (groupInfoSummary.chartBMI.avgStartBMI) {
                            this.chartBMI.series[0].data[0] = formatData('chart', groupInfoSummary.chartBMI.avgStartBMI);
                            this.chartBMI.series[1].data[0] = formatData('chart', groupInfoSummary.chartBMI.avgEndBMI);
                            this.chartBMI.percent = this.calculatePercentChanges(
                                groupInfoSummary.chartBMI.avgStartBMI,
                                groupInfoSummary.chartBMI.avgEndBMI,
                            );
                            this.chartBMI.maxY = this.calculateMaxY(
                                groupInfoSummary.chartBMI.avgStartBMI,
                                groupInfoSummary.chartBMI.avgEndBMI,
                                this.chartBMI.maxY,
                                5
                            );
                            this.chartBMI.noData = false;
                        }

                        //chart pressure
                        this.chartPressure.noData = true;
                        this.chartPressure.series[0].data = [];
                        this.chartPressure.series[1].data = [];
                        this.chartPressure.percent = '';
                        if (groupInfoSummary.chartPressure.avgStartPressureUpper) {
                            this.chartPressure.series[0].data[0] = formatData('chart', groupInfoSummary.chartPressure.avgStartPressureUpper);
                            this.chartPressure.series[0].data[1] = formatData('chart', groupInfoSummary.chartPressure.avgStartPressureLower);
                            this.chartPressure.series[1].data[0] = formatData('chart', groupInfoSummary.chartPressure.avgEndPressureUpper);
                            this.chartPressure.series[1].data[1] = formatData('chart', groupInfoSummary.chartPressure.avgEndPressureLower);
                            this.chartPressure.percent = this.calculatePercentChanges(
                                groupInfoSummary.chartPressure.avgStartPressureUpper,
                                groupInfoSummary.chartPressure.avgEndPressureUpper,
                            );
                            this.chartPressure.maxY = this.calculateMaxY(
                                groupInfoSummary.chartPressure.avgStartPressureUpper,
                                groupInfoSummary.chartPressure.avgEndPressureUpper,
                                this.chartPressure.maxY,
                                15
                            );
                            this.chartPressure.noData = false;
                        }

                        //chart LDL
                        this.chartLDL.noData = true;
                        this.chartLDL.series[0].data = [];
                        this.chartLDL.percent = '';
                        if (groupInfoSummary.chartLDL.avgStartLDL) {
                            this.chartLDL.series[0].data[0] = formatData('chart', groupInfoSummary.chartLDL.avgStartLDL);
                            this.chartLDL.series[1].data[0] = formatData('chart', groupInfoSummary.chartLDL.avgEndLDL);
                            this.chartLDL.percent = this.calculatePercentChanges(
                                groupInfoSummary.chartLDL.avgStartLDL,
                                groupInfoSummary.chartLDL.avgEndLDL,
                            );
                            this.chartLDL.maxY = this.calculateMaxY(
                                groupInfoSummary.chartLDL.avgStartLDL,
                                groupInfoSummary.chartLDL.avgEndLDL,
                                this.chartLDL.maxY,
                                20
                            );
                            this.chartLDL.noData = false;
                        }

                        //chart FBS
                        this.chartFBS.series[0].data = [];
                        this.chartFBS.noData = true;
                        this.chartFBS.percent = '';
                        if (groupInfoSummary.chartFBS.avgStartFBS) {
                            this.chartFBS.series[0].data[0] = formatData('chart', groupInfoSummary.chartFBS.avgStartFBS);
                            this.chartFBS.series[1].data[0] = formatData('chart', groupInfoSummary.chartFBS.avgEndFBS);
                            this.chartFBS.percent = this.calculatePercentChanges(
                                groupInfoSummary.chartFBS.avgStartFBS,
                                groupInfoSummary.chartFBS.avgEndFBS,
                            );
                            this.chartFBS.maxY = this.calculateMaxY(
                                groupInfoSummary.chartFBS.avgStartFBS,
                                groupInfoSummary.chartFBS.avgEndFBS,
                                this.chartFBS.maxY,
                                20
                            );
                            this.chartFBS.noData = false;
                        }

                        this.userList = groupInfoSummary.userList;
                        this.exportData = groupInfoSummary.rowExport;
                    }

                    this.loading = false;
                })();
            }
        },
        merge(a, b, prop) {
            var reduced = [];
            for (var i = 0; i < a.length; i++) {
                var aitem = a[i];
                var found = false;
                for (var ii = 0; ii < b.length; ii++) {
                    if (aitem[prop] === b[ii][prop]) {
                        found = true;
                        break;
                    }
                }
                if (!found) {
                    reduced.push(aitem);
                }
            }
            return reduced.concat(b);

        }
    }
};
</script>
